import { create } from 'zustand';

/**
 * Client-only UI state for the in-header search overlay: whether it's open and
 * the current (un-debounced) query. The overlay replaces the category tab row
 * with a full-width search field and a portaled results panel; closing it clears
 * the query so it always reopens empty. Server data (results, popular, related)
 * lives in React Query, not here; recent searches live in the search-history store.
 */
interface SearchOverlayState {
  isOpen: boolean;
  query: string;
  open: () => void;
  close: () => void;
  setQuery: (query: string) => void;
}

const useSearchOverlayStore = create<SearchOverlayState>((set) => ({
  isOpen: false,
  query: '',
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false, query: '' }),
  setQuery: (query) => set({ query }),
}));

/** Select whether the search overlay is open. */
export const useSearchOverlayOpen = (): boolean => useSearchOverlayStore((s) => s.isOpen);
/** Select the action that opens the search overlay. */
export const useOpenSearchOverlay = (): (() => void) => useSearchOverlayStore((s) => s.open);
/** Select the action that closes the search overlay (also clears the query). */
export const useCloseSearchOverlay = (): (() => void) => useSearchOverlayStore((s) => s.close);
/** Select the current search query. */
export const useSearchQuery = (): string => useSearchOverlayStore((s) => s.query);
/** Select the action that updates the search query. */
export const useSetSearchQuery = (): ((query: string) => void) => useSearchOverlayStore((s) => s.setQuery);

/** Imperative reset — for use outside React (e.g. logout, test setup). */
export const resetSearchOverlay = (): void => useSearchOverlayStore.setState({ isOpen: false, query: '' });
